import React, { useMemo } from 'react'
import { Badge, Row, Col } from 'react-bootstrap'
import { Model } from '../../../../types/models/model'
import { Brain, ChevronDown } from 'lucide-react'
import { FaCheck, FaSpinner } from 'react-icons/fa'
import { Project } from '../../../../types/models/project'
import {
  ModelState,
  NormalizedModelStatus,
  DeploymentStatus,
  DeploymentStatusLower,
  normalizeModelStatus,
  getModelStatusVariant,
  normalizeDeploymentStatusLower,
  getDeploymentVariant,
  getDeploymentStatusText,
} from '../../../../utils/modelStatus'
import { buildKeyMetrics, type ModelMetrics } from '../../../../utils/metrics'

interface CompactModelCardProps {
  model: Model
  project?: Project
  borderColor?: string
  showSelector?: boolean
  onShowSelector?: () => void
  iterationNumber?: number
  isIteration?: boolean
}

const CompactModelCard: React.FC<CompactModelCardProps> = ({
  model,
  project,
  borderColor,
  showSelector = false,
  onShowSelector,
  iterationNumber,
  isIteration = false,
}) => {
  const hexToRgba = (hex: string, alpha: number) => {
    const match = hex.replace('#', '')
    const bigint = parseInt(
      match.length === 3
        ? match
            .split('')
            .map((c) => c + c)
            .join('')
        : match,
      16
    )
    const r = (bigint >> 16) & 255
    const g = (bigint >> 8) & 255
    const b = bigint & 255
    return `rgba(${r}, ${g}, ${b}, ${alpha})`
  }
  // Normalized, memoized statuses
  const modelStatus: NormalizedModelStatus = useMemo(
    () => normalizeModelStatus(model.state as ModelState | undefined),
    [model.state]
  )
  const statusVariant = useMemo(() => getModelStatusVariant(modelStatus), [modelStatus])

  const deploymentStatusLower: DeploymentStatusLower = useMemo(
    () => normalizeDeploymentStatusLower(model.deployment?.status as DeploymentStatus | undefined),
    [model.deployment?.status]
  )
  const deploymentVariant = useMemo(
    () => getDeploymentVariant(deploymentStatusLower),
    [deploymentStatusLower]
  )
  const deploymentStatusText = useMemo(
    () => getDeploymentStatusText(model.deployment?.status as DeploymentStatus | undefined),
    [model.deployment?.status]
  )

  // Calculate key metrics for display (memoized)
  const keyMetrics = useMemo(
    () => buildKeyMetrics(project?.annotation_type, model.metrics as ModelMetrics),
    [project?.annotation_type, model.metrics]
  )

  // Use green border for iteration cards (memoized)
  const cardBorderColor = useMemo(
    () => (isIteration ? '#34d399' : borderColor || '#9333ea'),
    [isIteration, borderColor]
  )

  const cardTint = useMemo(() => hexToRgba(cardBorderColor || '#9333ea', 0.15), [cardBorderColor])

  // Get iteration number if not provided (memoized and safe)
  const parsedIterationNumber = useMemo(() => {
    if (iterationNumber !== undefined) return iterationNumber
    if (!model.parent_id) return null
    const match = model.name.match(/#(\d+)/)
    const num = match ? Number(match[1]) : NaN
    return Number.isFinite(num) ? num : null
  }, [iterationNumber, model.parent_id, model.name])

  return (
    <div
      className="bg-dark rounded-2 p-2 mb-2 position-relative d-flex flex-column gap-1"
      style={{
        borderColor: cardBorderColor,
        boxShadow: `0 0 0 1px ${cardBorderColor}33`,
        backgroundImage: `linear-gradient(0deg, ${cardTint}, ${cardTint})`,
      }}
    >
      <div
        className={`d-flex align-items-center ${
          showSelector && onShowSelector ? 'justify-content-between' : 'justify-content-start'
        } mb-1 pb-1 border-bottom border-dark`}
        style={{ minHeight: '60px' }}
      >
        <div className="d-flex align-items-start gap-2 flex-grow-1" style={{ minWidth: 0 }}>
          <div
            className="d-flex align-items-center justify-content-center rounded-1 text-white shadow-sm model-icon-box"
            style={{
              background: cardBorderColor
                ? `linear-gradient(135deg, ${cardBorderColor}, ${cardBorderColor}80)`
                : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            }}
          >
            <Brain size={16} aria-hidden="true" />
          </div>
          <div className="d-flex flex-column flex-grow-1" style={{ minWidth: 0 }}>
            <h6 className="m-0 mb-1 text-white text-truncate" title={model.name}>
              {model.name}
            </h6>
            <div className="d-flex align-items-center gap-1 flex-wrap" style={{ minHeight: 24 }}>
              {isIteration ? (
                <>
                  <Badge
                    bg={statusVariant}
                    className="text-uppercase fw-semibold rounded-pill py-1 px-2 small d-inline-flex align-items-center"
                    aria-label={`Model status: ${modelStatus}`}
                  >
                    {modelStatus === 'training' && (
                      <FaSpinner style={{ marginRight: '0.25rem' }} aria-hidden="true" />
                    )}
                    {modelStatus === 'trained' && (
                      <FaCheck style={{ marginRight: '0.25rem' }} aria-hidden="true" />
                    )}
                    {modelStatus}
                  </Badge>
                  {parsedIterationNumber && (
                    <span
                      className="rounded-pill d-inline-flex align-items-center"
                      style={{
                        background: 'rgba(52, 211, 153, 0.15)',
                        color: '#34d399',
                        padding: '0.2rem 0.375rem',
                        fontSize: '0.65rem',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.25px',
                        border: '1px solid rgba(52, 211, 153, 0.3)',
                        height: 24,
                      }}
                    >
                      Iteration #{parsedIterationNumber}
                    </span>
                  )}
                </>
              ) : (
                <>
                  <Badge
                    bg={statusVariant}
                    className="text-uppercase fw-semibold rounded-pill py-1 px-2 small d-inline-flex align-items-center"
                    aria-label={`Model status: ${modelStatus}`}
                  >
                    {modelStatus}
                  </Badge>
                  <Badge
                    bg={deploymentVariant}
                    className="text-uppercase fw-semibold rounded-pill py-1 px-2 small"
                    aria-label={`Deployment status: ${deploymentStatusText}`}
                  >
                    {deploymentStatusText}
                  </Badge>
                </>
              )}
            </div>
          </div>
        </div>

        {showSelector && onShowSelector && (
          <button
            type="button"
            onClick={onShowSelector}
            aria-label="Open model selector"
            className="btn btn-dark btn-sm d-flex align-items-center gap-1"
          >
            <ChevronDown size={12} aria-hidden="true" />
            Change
          </button>
        )}
      </div>

      <Row xs={4} className="g-1">
        {keyMetrics.map((metric) => (
          <Col key={metric.label}>
            <div
              className="text-center d-flex flex-column justify-content-center align-items-center rounded-2 py-1 bg-dark"
              style={{ minHeight: 45 }}
            >
              <div className="fw-bold text-white small text-truncate" title={metric.value}>
                {metric.value}
              </div>
              <div
                className="text-secondary text-uppercase text-truncate"
                title={metric.label}
                style={{ fontSize: '0.7rem', letterSpacing: '0.25px' }}
              >
                {metric.label}
              </div>
            </div>
          </Col>
        ))}
      </Row>
    </div>
  )
}

export default React.memo(CompactModelCard)
